/**
 * CORE-589 — [FE-Web] «Live-сессия — отображение на платформе».
 *
 * Single source of truth for how the UNIFIED Live-session (carried by the
 * WatchParty / `RoomResponse` entity, epic CORE-541) is presented on the
 * platform's EXTERNAL discovery surfaces — the home «Сейчас в эфире» strip
 * (StartingSoon / PracticeRoomsPreview), the general feed (FeedCard), and a
 * master's events (MasterEventsSection). Card-tap routing itself already runs
 * through RoomAnnouncement → the flag-gated LiveSessionEntry (CORE-586); this
 * module owns the *vocabulary* and the *state classification* those surfaces
 * render.
 *
 * PURE by design (no React / RN imports) so the rules unit-test in the
 * node/ts-jest env, mirroring liveSessionAnalytics.ts / recordingLifecycle.ts.
 * Call-sites gate the unified vocabulary on `useUnifiedLiveEnabled()` so a
 * flag-off bundle is byte-identical to the legacy labels. The privacy gate for
 * discovery lists lives in `filterPublicRooms` (shared by every public strip),
 * not here.
 */
import type { RoomResponse } from '../types/auth';
import { END_REASONS } from '../services/endReasons';

/** The one canonical term for the unified entity (general requirement AC). */
export const LIVE_SESSION_TERM = 'Live-сессия';

/** External-facing lifecycle state shown on a card / hero. */
export type LiveSessionDisplayState = 'scheduled' | 'live' | 'completed' | 'cancelled';

/**
 * Classify a room for external display. `Active` → live, `Scheduled` →
 * scheduled; an `Ended` room splits into `cancelled` vs `completed` keyed off
 * the canonical end-reason taxonomy (endReasons.ts ↔ BE EndReasons.cs). Only an
 * explicit `cancelled` reason reads as cancelled — every other terminal reason
 * (host_ended / duration_elapsed / empty / …) is a completed session.
 */
export function liveSessionDisplayState(
  room: Pick<RoomResponse, 'status' | 'endReason'>,
): LiveSessionDisplayState {
  if (room.status === 'Active') return 'live';
  if (room.status === 'Scheduled') return 'scheduled';
  return room.endReason === END_REASONS.Cancelled ? 'cancelled' : 'completed';
}

const STATE_LABEL_RU: Record<LiveSessionDisplayState, string> = {
  scheduled: 'Запланирована',
  live: 'LIVE',
  completed: 'Завершена',
  cancelled: 'Отменена',
};

/** RU status label for a display state (used on cards / status pills). */
export function liveSessionStateLabelRu(state: LiveSessionDisplayState): string {
  return STATE_LABEL_RU[state];
}

/**
 * Unified card category for the now-live / starting-soon strip. Under the
 * unified flag the room's former «Live-практика» label (AMB-220) collapses into
 * the one «Live-сессия» term. Mirrors the media split the legacy mapper used:
 * video / audio sub-label, else the bare term.
 */
export function liveSessionCategoryRu(mediaType: string | null | undefined): string {
  if (mediaType === 'VideoPractice') return `${LIVE_SESSION_TERM} · видео`;
  if (mediaType === 'AudioPractice') return `${LIVE_SESSION_TERM} · аудио`;
  return LIVE_SESSION_TERM;
}

/**
 * Unified feed-badge pill text (upper-case, matching the existing FeedCard pill
 * style). Both the legacy live-broadcast («LIVE-СЕССИЯ») and the room/practice
 * («LIVE-ПРАКТИКА») kinds become the single «LIVE-СЕССИЯ» vocabulary;
 * recordings become «ЗАПИСЬ LIVE-СЕССИИ». Returns null for non-Live feed kinds
 * (offline / saved / practice_uploaded) so they keep their own badge.
 */
export function liveSessionFeedBadge(kind: string): string | null {
  switch (kind) {
    case 'live_scheduled':
    case 'room_scheduled':
      return 'LIVE-СЕССИЯ';
    case 'live_recorded':
    case 'room_recorded':
      return 'ЗАПИСЬ LIVE-СЕССИИ';
    default:
      return null;
  }
}
